import { Link } from 'react-router-dom';
import { ArrowRight, Check, MoveRight } from 'lucide-react';
import type { ChatCard, CommitDescriptor } from '../../types/chat';
import { Button } from '../ui/button';
import { entityVisual } from './entity-visuals';
import { ChatDisplayCard } from './ChatDisplayCard';

const ACTION_LABEL: Record<ChatCard['action'], string> = {
  create: 'Create',
  update: 'Updated',
  delete: 'Delete',
  link: 'Link',
  view: 'View',
};

export function ChatCardView({
  card,
  onConfirm,
  onCancel,
  confirming,
  onUndo,
  undoing,
  onRowAction,
}: {
  card: ChatCard;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirming?: boolean;
  onUndo?: () => void;
  undoing?: boolean;
  onRowAction?: (commit: CommitDescriptor) => Promise<void>;
}) {
  // Display cards (agenda, tasks, contact, Brain, pipeline, alerts) render through
  // their own component — no confirm/undo, but rows may carry one-tap actions.
  if (card.kind === 'display') {
    return <ChatDisplayCard card={card} onRowAction={onRowAction} />;
  }

  const isConfirm = card.kind === 'confirm';
  const v = entityVisual(card.entityType);

  return (
    <div className="group relative overflow-hidden rounded-xl border border-border/60 bg-card/80 p-3 pl-4 text-sm shadow-sm transition-colors motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-1 motion-safe:duration-200">
      {/* Accent spine */}
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${v.bar}`} />

      {/* Header row */}
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${v.chip}`}>
            <v.Icon className={`h-4 w-4 ${v.icon}`} aria-hidden />
          </span>
          <span className="min-w-0 truncate font-semibold">{card.title}</span>
        </div>

        {isConfirm ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground capitalize">
            {ACTION_LABEL[card.action]} {card.entityType.replace('_', ' ')}
          </span>
        ) : (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300 ring-1 ring-emerald-500/25">
            <Check className="h-3 w-3" aria-hidden />
            {ACTION_LABEL[card.action]}
          </span>
        )}
      </div>

      {/* Changes — before → after for updates */}
      {card.changes && card.changes.length > 0 && (
        <dl className="mb-2 space-y-1.5">
          {card.changes.map((ch) => (
            <div key={ch.label} className="flex gap-2">
              <dt className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                {ch.label}
              </dt>
              <dd className="flex min-w-0 flex-wrap items-center gap-1.5 font-medium">
                <span className="min-w-0 break-words text-muted-foreground line-through">
                  {ch.before || '(empty)'}
                </span>
                <MoveRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                <span className="min-w-0 break-words">{ch.after || '(empty)'}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Fields */}
      {!card.changes && card.fields.length > 0 && (
        <dl className="space-y-1.5">
          {card.fields.map((f) => (
            <div key={f.label} className="flex gap-2">
              <dt className="w-24 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
                {f.label}
              </dt>
              <dd className="min-w-0 break-words font-medium">
                {f.label.toLowerCase().includes('code') || f.label.toLowerCase().includes('id') ? (
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{f.value}</code>
                ) : (
                  f.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Actions */}
      {isConfirm ? (
        <div className="mt-3 flex gap-2">
          <Button
            size="sm"
            className="rounded-full"
            onClick={onConfirm}
            disabled={confirming || !onConfirm}
          >
            <Check className="mr-1 h-3.5 w-3.5" aria-hidden />
            {confirming ? 'Saving…' : 'Confirm'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-full"
            onClick={onCancel}
            disabled={confirming || !onCancel}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-3">
          {card.link && (
            <Link
              to={card.link}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20 cursor-pointer"
            >
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          )}
          {onUndo && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 rounded-full px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={onUndo}
              disabled={undoing}
            >
              {undoing ? 'Undoing…' : 'Undo'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
